import { useState } from 'react';
import { Modal, Button } from '@components/ui';
import { CalendarDays, CreditCard } from 'lucide-react';

/**
 * OdemePlanMiniModal — Senaryo A için küçük "Ödeme Planı Oluştur?" modalı
 *
 * Malzeme siparişi başarıyla kaydedildikten sonra otomatik açılır.
 * Kullanıcı vade tarihi ve ödeme aracı seçer → scheduled_payment oluşturulur.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   onConfirm: ({ nextDueDate, paymentSubtype }) => void
 *   amount: number — malzeme siparişinin KDV dahil tutarı
 *   companyName: string — tedarikçi adı
 */
export default function OdemePlanMiniModal({ isOpen, onClose, onConfirm, amount, companyName }) {
    const [nextDueDate, setNextDueDate] = useState('');
    const [paymentSubtype, setPaymentSubtype] = useState('havale');

    const formatCurrency = (n) => new Intl.NumberFormat('tr-TR', {
        style: 'currency', currency: 'TRY', minimumFractionDigits: 0,
    }).format(n || 0);

    const handleConfirm = () => {
        if (!nextDueDate) return;
        onConfirm({ nextDueDate, paymentSubtype });
        setNextDueDate('');
        setPaymentSubtype('havale');
    };

    const handleSkip = () => {
        setNextDueDate('');
        setPaymentSubtype('havale');
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleSkip}
            title="Ödeme Planı Oluştur?"
            size="sm"
            footer={
                <>
                    <Button variant="ghost" onClick={handleSkip}>Şimdi Değil</Button>
                    <Button variant="primary" onClick={handleConfirm} disabled={!nextDueDate}>
                        Ödeme Planı Oluştur
                    </Button>
                </>
            }
        >
            <div className="space-y-4">
                <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                    <p className="text-sm text-text-secondary">
                        <span className="font-semibold text-text-primary">{companyName}</span> firmasına{' '}
                        <span className="font-bold text-primary">{formatCurrency(amount)}</span> tutarında
                        ödeme planı oluşturulsun mu?
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                        Ödeme planı, Planlı Ödemeler sayfasında takip edilir ve Cari Hesaba bağlanır.
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">
                        <CalendarDays size={13} className="inline mr-1" />
                        Vade Tarihi <span className="text-error">*</span>
                    </label>
                    <input
                        type="date"
                        value={nextDueDate}
                        onChange={e => setNextDueDate(e.target.value)}
                        className="w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">
                        <CreditCard size={13} className="inline mr-1" />
                        Ödeme Yöntemi
                    </label>
                    <select
                        value={paymentSubtype}
                        onChange={e => setPaymentSubtype(e.target.value)}
                        className="w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                    >
                        <option value="nakit">Nakit</option>
                        <option value="havale">Banka Havalesi / EFT</option>
                        <option value="kart">Kredi / Banka Kartı</option>
                        <option value="cek">Kendi Çekimiz</option>
                        <option value="senet">Senet</option>
                    </select>
                </div>
            </div>
        </Modal>
    );
}
